import React from 'react';
import { Mail, Phone } from 'lucide-react';
import './Student360.css';

/** +998901234567 -> "+998 90 123 45 67"; any other number is shown as stored. */
export function formatPhone(phone) {
  const text = String(phone || '').trim();
  const match = /^\+?998(\d{2})(\d{3})(\d{2})(\d{2})$/.exec(text.replace(/[\s()-]/g, ''));
  return match ? `+998 ${match[1]} ${match[2]} ${match[3]} ${match[4]}` : text;
}

/** tel: link of a stored number, digits and a leading plus only; null when nothing dialable is left. */
export function telHref(phone) {
  const text = String(phone || '').trim();
  const digits = text.replace(/\D/g, '');
  if (!digits) return null;
  return `tel:${text.startsWith('+') ? '+' : ''}${digits}`;
}

// One @, no spaces, nothing that quotes a name or separates recipients in a mail client.
const EMAIL_PATTERN = /^[^\s@<>"(),;:\\[\]]+@[^\s@<>"(),;:\\[\]]+$/;

/**
 * mailto: link of a plausible address; null for anything else (it is then shown as plain text).
 * The address is what the student typed, so it is data, not a URL: `?`, `&`, `=`, `%` and `#` are
 * percent-encoded, which keeps an address such as `a?bcc=someone@x.com` (the API accepts it) from
 * adding a bcc, a subject or a body to the message a staff member is about to write.
 */
export function mailHref(email) {
  const text = String(email || '').trim();
  if (!EMAIL_PATTERN.test(text)) return null;
  return `mailto:${text.replace(/[?&=%#]/g, (char) => encodeURIComponent(char))}`;
}

/**
 * Phone and email of a student the viewer may contact: tel: and mailto: links. A missing value is
 * left out; with neither, one em dash. Rendered only by callers whose response has the CT keys.
 */
function ContactLinks({ phone, email, stacked = true }) {
  const phoneText = typeof phone === 'string' ? phone.trim() : '';
  const emailText = typeof email === 'string' ? email.trim() : '';
  if (!phoneText && !emailText) return <span className="s360-muted">—</span>;

  const phoneHref = phoneText ? telHref(phoneText) : null;
  const emailHref = emailText ? mailHref(emailText) : null;

  return (
    <span className={`s360-contact ${stacked ? 'is-stacked' : ''}`}>
      {phoneText && (
        <span className="s360-contact-item">
          <Phone size={13} aria-hidden="true" />
          {phoneHref
            ? <a href={phoneHref} className="s360-contact-link">{formatPhone(phoneText)}</a>
            : <span>{formatPhone(phoneText)}</span>}
        </span>
      )}
      {emailText && (
        <span className="s360-contact-item">
          <Mail size={13} aria-hidden="true" />
          {emailHref
            ? <a href={emailHref} className="s360-contact-link" title={emailText}>{emailText}</a>
            : <span title={emailText}>{emailText}</span>}
        </span>
      )}
    </span>
  );
}

export default ContactLinks;
